// ============================================================
// Console data store. Fetches the operator/partner's real book of business
// from /v1/admin/* and exposes it via hooks. Handles the auth phase so the
// app can show a login screen when there's no session.
//
// Workspace selection: the operator can switch between "all" and any
// workspace; a partner is locked to their own.
// ============================================================
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Activity, Client, Integration, PlanDef, RevPoint, Workspace } from './types';
import { api, UnauthorizedError, type AdminMe } from './api';

export type Phase = 'loading' | 'anon' | 'ready' | 'error';

interface DataCtx {
  me: AdminMe | null;
  phase: Phase;
  error?: string;
  clients: Client[];
  workspaces: Workspace[];
  activity: Activity[];
  revenue: RevPoint[];
  plans: PlanDef[];
  integrations: Integration[];
  current: string;
  setCurrent: (s: string) => void;
  reload: () => void;
}

const C = createContext<DataCtx | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | undefined>(undefined);
  const [clients, setClients] = useState<Client[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [revenue, setRevenue] = useState<RevPoint[]>([]);
  const [plans, setPlans] = useState<PlanDef[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [current, setCurrent] = useState<string>('all');

  const load = useCallback(async () => {
    setPhase('loading');
    setError(undefined);
    try {
      const m = await api.me();
      setMe(m);
      // Partner is scoped to their own workspace; operator defaults to "all".
      setCurrent(m.role === 'partner' ? m.workspace.id : 'all');
      const [cl, ws, ov, cfg] = await Promise.all([api.clients(), api.workspaces(), api.overview(), api.config()]);
      setClients(cl);
      setWorkspaces(ws);
      setActivity(ov.activity);
      setRevenue(ov.revenue);
      setPlans(cfg.plans);
      setIntegrations(cfg.integrations);
      setPhase('ready');
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        setMe(null);
        setPhase('anon');
      } else {
        setError(e instanceof Error ? e.message : String(e));
        setPhase('error');
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <C.Provider value={{ me, phase, error, clients, workspaces, activity, revenue, plans, integrations, current, setCurrent, reload: load }}>
      {children}
    </C.Provider>
  );
}

function useData(): DataCtx {
  const c = useContext(C);
  if (!c) throw new Error('useData must be used inside <DataProvider>');
  return c;
}

/** Auth/loading phase + reload, for the top-level gate. */
export function useDataPhase() {
  const { phase, error, reload, me } = useData();
  return { phase, error, reload, me };
}

export function useMe(): AdminMe | null {
  return useData().me;
}

/** Refetch all Console data (after a mutation that affects the lists). */
export function useReload(): () => void {
  return useData().reload;
}

/** Workspace selection. Same shape the views already use. */
export function useWorkspace() {
  const { current, setCurrent } = useData();
  return { current, setCurrent };
}

export function useWorkspaces(): Workspace[] {
  return useData().workspaces;
}

/** Lookup helper mirroring the old mock `workspaceById`. */
export function useWorkspaceById(): (id: string) => Workspace | undefined {
  const ws = useWorkspaces();
  return (id: string) => ws.find((w) => w.id === id);
}

export function useOverview() {
  const { activity, revenue } = useData();
  return { activity, revenue };
}

/** Plans + live integration status (from /v1/admin/config). */
export function useConfig() {
  const { plans, integrations } = useData();
  return { plans, integrations };
}

/** Every client the admin can see (unfiltered by the workspace switcher). */
export function useAllClients(): Client[] {
  return useData().clients;
}

/** Clients in the current workspace selection. */
export function useClients(): Client[] {
  const { clients, current } = useData();
  return current === 'all' ? clients : clients.filter((c) => c.workspaceId === current);
}
